import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, ClipboardList, Users, UserX } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, EmptyState, PageHeader, Skeleton } from "../components/ui";
import { LEAVE_LABEL, LEAVE_TONE } from "../lib/people";
import {
  PEOPLE_METRIC_LABEL,
  PEOPLE_METRICS,
  metricInputsFrom,
  peopleMetricCounts,
  type PeopleMetric,
} from "../lib/peopleMetrics";
import type { ReactNode } from "react";

/**
 * The People dashboard: awareness and action, not management.
 *
 * It answers "who is in today?" and "what needs a decision?", then links to the
 * module that owns the work. It owns no workflow itself, and every card opens
 * the list behind its number -- both read the same inputs through
 * `lib/peopleMetrics`, so a count can never disagree with the list it opens.
 */
export function PeopleDashboardPage() {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;
  const today = useMemo(() => localDate(timezone), [timezone]);

  const employees = useQuery({
    queryKey: ["people-metric-employees"],
    queryFn: ({ signal }) => api.employeeList({ page_size: 200, ordering: "last_name" }, signal),
    staleTime: 5 * 60_000,
  });
  const attendance = useQuery({
    queryKey: ["attendance", "dashboard", today],
    queryFn: ({ signal }) => api.attendancePage({ date: today, page_size: 200 }, signal),
  });
  const shifts = useQuery({
    queryKey: ["roster", "dashboard", today],
    queryFn: ({ signal }) => api.shiftsPage({ from: today, to: today, page_size: 200 }, signal),
  });
  const approvedLeave = useQuery({
    queryKey: ["leave-requests", "dashboard", "approved"],
    queryFn: ({ signal }) => api.leaveRequests({ status: "approved", page_size: 200 }, signal),
  });
  const pendingLeave = useQuery({
    queryKey: ["leave-requests", "dashboard", "pending"],
    queryFn: ({ signal }) => api.leaveRequests({ status: "pending", page_size: 5 }, signal),
  });

  const inputs = metricInputsFrom({
    employees: employees.data?.results ?? [],
    attendance: attendance.data?.results ?? [],
    leave: approvedLeave.data?.results ?? [],
    shifts: shifts.data?.results ?? [],
  });
  const counts = peopleMetricCounts(inputs, today);

  const pending = employees.isPending || attendance.isPending || shifts.isPending || approvedLeave.isPending;
  const error = employees.error || attendance.error || shifts.error || approvedLeave.error;

  return (
    <>
      <PageHeader
        title="People dashboard"
        subtitle="Who is in today, and what needs a decision."
      />

      {error ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error instanceof ApiError ? error.message : "Some figures could not be loaded. Please try again."}
        </p>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {PEOPLE_METRICS.map((metric) => (
          <StatLink
            key={metric}
            metric={metric}
            value={counts[metric]}
            pending={pending}
            unavailable={Boolean(error)}
            to={`/people/employees?metric=${metric}&date=${today}`}
          />
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-xs">
          <h2 className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-900">
            Awaiting approval
            <Link
              to="/people/leave?status=pending"
              className="text-xs font-normal text-slate-500 hover:text-slate-700"
            >
              Review pending →
            </Link>
          </h2>
          {pendingLeave.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : pendingLeave.isError ? (
            <EmptyState title="Could not load leave" body="Please try again." />
          ) : (pendingLeave.data?.results ?? []).length === 0 ? (
            <EmptyState title="Nothing queued" body="No leave requests waiting on a decision." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {(pendingLeave.data?.results ?? []).slice(0, 4).map((leave) => (
                <li key={leave.id}>
                  <Link
                    to={leave.employee ? `/people/employees/${leave.employee}` : "/people/leave?status=pending"}
                    className="-mx-2 flex items-center justify-between rounded-md px-2 py-2.5 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-900">{leave.employee_name}</span>
                      <span className="block text-xs text-slate-500">
                        {leave.from_date} → {leave.to_date} · {leave.days} day
                        {leave.days === "1" ? "" : "s"}
                      </span>
                    </span>
                    <Badge tone={LEAVE_TONE[leave.status ?? "pending"] ?? "slate"}>{LEAVE_LABEL[leave.status ?? "pending"]}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-xs">
          <h2 className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-900">
            Today's roster
            <Link to="/people/roster" className="text-xs font-normal text-slate-500 hover:text-slate-700">
              Roster →
            </Link>
          </h2>
          {shifts.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : shifts.isError ? (
            <EmptyState title="Could not load the roster" body="Please try again." />
          ) : (shifts.data?.results ?? []).length === 0 ? (
            <EmptyState title="Nothing rostered" body="No shifts published for today." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {(shifts.data?.results ?? []).slice(0, 6).map((shift) => (
                <li key={shift.id}>
                  <Link
                    to={`/people/employees/${shift.employee}`}
                    aria-label={`Open ${shift.employee_name}'s shift`}
                    className="-mx-2 flex items-center justify-between rounded-md px-2 py-2.5 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                  >
                    <span className="text-sm text-slate-800">{shift.employee_name}</span>
                    <span className="text-xs tabular-nums text-slate-500">
                      {shift.start_time.slice(0, 5)}–{shift.end_time.slice(0, 5)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

const METRIC_ICON: Record<PeopleMetric, ReactNode> = {
  team: <Users size={16} />,
  present: <CalendarDays size={16} />,
  on_leave: <ClipboardList size={16} />,
  not_checked_in: <UserX size={16} />,
};

/**
 * A metric card that is a real link. The whole card is the target, so it is
 * reachable by keyboard and announces what it opens; the "View list" affordance
 * is decorative and aria-hidden to keep the accessible name clean.
 */
function StatLink({
  metric,
  value,
  pending,
  unavailable,
  to,
}: {
  metric: PeopleMetric;
  value: number;
  pending: boolean;
  unavailable: boolean;
  to: string;
}) {
  return (
    <Link
      to={to}
      aria-label={`${PEOPLE_METRIC_LABEL[metric]}: ${value}. Open the list.`}
      className="group block rounded-lg border border-slate-200 bg-white p-4 shadow-xs transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
    >
      <div className="flex items-center gap-2 text-slate-500">
        {METRIC_ICON[metric]}
        <span className="text-xs font-medium uppercase tracking-wide">{PEOPLE_METRIC_LABEL[metric]}</span>
      </div>
      {pending ? (
        <Skeleton className="mt-2 h-7 w-12" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{unavailable ? "—" : value}</p>
      )}
      <span
        aria-hidden="true"
        className="mt-1 inline-flex items-center gap-1 text-xs text-slate-400 group-hover:text-slate-600"
      >
        View list <ArrowRight size={12} />
      </span>
    </Link>
  );
}

function localDate(timeZone?: string | null): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone ?? undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}
